/**
 * TCI-session SecureLink consent helpers — mint token, self-redeem, grant checks.
 */

import { db } from "services/firebase";
import {
  SHAREABLE_SECTIONS,
  createShareToken,
  consumeShareCodeAndFetchRecord,
  TCI_SESSION_TOKEN_CONTEXT,
  buildQrPayload,
  formatManualCode,
} from "services/healthRecordsShareService";
import {
  getActiveCmrDataFlow,
  getCmrField,
  sectionsFromCmrDataFlow,
} from "constants/cmrFieldRegistry";

export { buildQrPayload, formatManualCode };

export const TCI_SESSION_GRANT_PREFIX = "tciSession_";

/**
 * True when the TCI has any active cmrDataFlow (inbound and/or outbound).
 * medrec-intake is excluded by callers that check tciId separately.
 */
export function tciRequiresSecureLinkConsent(tciDoc) {
  const flow = getActiveCmrDataFlow(tciDoc || {});
  return (flow.inbound?.length || 0) + (flow.outbound?.length || 0) > 0;
}

/**
 * Fixed topic list for the consent UI (labels from SHAREABLE_SECTIONS).
 * Patient cannot remove topics — only consent or decline.
 */
export function getTciConsentTopics(tciDoc) {
  const sectionIds = sectionsFromCmrDataFlow(tciDoc);
  return sectionIds.map((id) => {
    const meta = SHAREABLE_SECTIONS.find((s) => s.id === id);
    return {
      id,
      label: meta?.label || id,
    };
  });
}

/**
 * Sections required for inbound projection (field → section).
 */
export function inboundSectionsForTci(tciDoc) {
  const { inbound } = getActiveCmrDataFlow(tciDoc || {});
  const seen = new Set();
  const out = [];
  for (const row of inbound) {
    const fieldId = typeof row?.field === "string" ? row.field.trim() : "";
    const section = getCmrField(fieldId)?.section;
    if (!section || seen.has(section)) continue;
    seen.add(section);
    out.push(section);
  }
  return out;
}

/**
 * Sections required for a single outbound field.
 */
export function outboundSectionForField(fieldId) {
  const section = getCmrField(
    typeof fieldId === "string" ? fieldId.trim() : "",
  )?.section;
  return section || null;
}

export function tciSessionGrantDocId(tciSessionId) {
  const id = typeof tciSessionId === "string" ? tciSessionId.trim() : "";
  return id ? `${TCI_SESSION_GRANT_PREFIX}${id}` : "";
}

/**
 * Client read of a tci-session grant (rules: reader or patient may read).
 * @returns {Promise<{ ok: boolean, reason?: string, grant?: object }>}
 */
export async function assertClientTciSessionGrant({
  tciSessionId,
  readerUid,
  patientUid,
  requiredSections = [],
} = {}) {
  const sessionId =
    typeof tciSessionId === "string" ? tciSessionId.trim() : "";
  const reader = typeof readerUid === "string" ? readerUid.trim() : "";
  const patient = typeof patientUid === "string" ? patientUid.trim() : "";
  if (!sessionId || !reader || !patient) {
    return { ok: false, reason: "invalid-args" };
  }

  const grantId = tciSessionGrantDocId(sessionId);
  let snap;
  try {
    snap = await db.collection("cmrConsentGrants").doc(grantId).get();
  } catch (err) {
    console.warn("[tciSessionConsent] grant read failed", err);
    return { ok: false, reason: "read-failed" };
  }

  if (!snap.exists) {
    return { ok: false, reason: "missing" };
  }

  const data = snap.data() || {};
  if (data.grantType !== "tci-session") {
    return { ok: false, reason: "wrong-type" };
  }
  if ((data.tciSessionId || "").trim() !== sessionId) {
    return { ok: false, reason: "session-mismatch" };
  }
  if ((data.clinicianUid || "").trim() !== reader) {
    return { ok: false, reason: "reader-mismatch" };
  }
  if ((data.patientUid || "").trim() !== patient) {
    return { ok: false, reason: "patient-mismatch" };
  }
  if (data.revokedAt != null) {
    return { ok: false, reason: "revoked" };
  }

  let expiresMs = null;
  if (data.expiresAt?.toMillis) {
    expiresMs = data.expiresAt.toMillis();
  } else if (data.expiresAt?.toDate) {
    expiresMs = data.expiresAt.toDate().getTime();
  } else if (data.expiresAt instanceof Date) {
    expiresMs = data.expiresAt.getTime();
  } else if (typeof data.expiresAt === "number") {
    expiresMs = data.expiresAt;
  }
  if (!expiresMs || Date.now() > expiresMs) {
    return { ok: false, reason: "expired" };
  }

  const allowed = new Set(
    Array.isArray(data.allowedSections) ? data.allowedSections : [],
  );
  if (allowed.size === 0) {
    return { ok: false, reason: "empty-sections" };
  }
  for (const section of requiredSections) {
    if (!allowed.has(section)) {
      return { ok: false, reason: "section-denied", missing: section };
    }
  }

  return { ok: true, grant: data, grantId };
}

export const STAFF_PROXY_CMR_GRANT_PREFIX = "staffProxyCmr_";
export const STAFF_PROXY_CMR_GRANT_TYPE = "staff-proxy-cmr";

export function staffProxyCmrGrantDocId(sessionId) {
  const id = typeof sessionId === "string" ? sessionId.trim() : "";
  return id ? `${STAFF_PROXY_CMR_GRANT_PREFIX}${id}` : "";
}

export function staffProxyCmrClinicalGrantDocId(sessionId) {
  const id = typeof sessionId === "string" ? sessionId.trim() : "";
  return id ? `${STAFF_PROXY_CMR_GRANT_PREFIX}${id}_clinical` : "";
}

/**
 * Client read of a VisitStart staff-proxy-cmr grant (rules: clinician or patient).
 * @returns {Promise<{ ok: boolean, reason?: string, grant?: object, grantId?: string }>}
 */
export async function assertClientStaffProxyCmrGrant({
  sessionId,
  staffUid,
  patientUid,
  requiredSections = [],
} = {}) {
  const sid = typeof sessionId === "string" ? sessionId.trim() : "";
  const staff = typeof staffUid === "string" ? staffUid.trim() : "";
  const patient = typeof patientUid === "string" ? patientUid.trim() : "";
  if (!sid || !staff || !patient) {
    return { ok: false, reason: "invalid-args" };
  }

  const candidateIds = [
    staffProxyCmrClinicalGrantDocId(sid),
    staffProxyCmrGrantDocId(sid),
  ];
  let last = { ok: false, reason: "missing" };
  for (const grantId of candidateIds) {
    let snap;
    try {
      snap = await db.collection("cmrConsentGrants").doc(grantId).get();
    } catch (err) {
      console.warn("[tciSessionConsent] staff-proxy grant read failed", err);
      last = { ok: false, reason: "read-failed", grantId };
      continue;
    }
    if (!snap.exists) {
      last = { ok: false, reason: "missing", grantId };
      continue;
    }

    const data = snap.data() || {};
    if (data.grantType !== STAFF_PROXY_CMR_GRANT_TYPE) {
      last = { ok: false, reason: "wrong-type", grantId };
      continue;
    }
    if ((data.sessionId || "").trim() !== sid) {
      last = { ok: false, reason: "session-mismatch", grantId };
      continue;
    }
    if ((data.clinicianUid || "").trim() !== staff) {
      last = { ok: false, reason: "reader-mismatch", grantId };
      continue;
    }
    if ((data.patientUid || "").trim() !== patient) {
      last = { ok: false, reason: "patient-mismatch", grantId };
      continue;
    }
    if (data.revokedAt != null) {
      last = { ok: false, reason: "revoked", grantId };
      continue;
    }

    let expiresMs = null;
    if (data.expiresAt?.toMillis) {
      expiresMs = data.expiresAt.toMillis();
    } else if (data.expiresAt?.toDate) {
      expiresMs = data.expiresAt.toDate().getTime();
    } else if (data.expiresAt instanceof Date) {
      expiresMs = data.expiresAt.getTime();
    } else if (typeof data.expiresAt === "number") {
      expiresMs = data.expiresAt;
    }
    if (!expiresMs || Date.now() > expiresMs) {
      last = { ok: false, reason: "expired", grantId };
      continue;
    }

    const allowed = new Set(
      Array.isArray(data.allowedSections) ? data.allowedSections : [],
    );
    if (allowed.size === 0) {
      last = { ok: false, reason: "empty-sections", grantId };
      continue;
    }
    let denied = null;
    for (const section of requiredSections) {
      if (!allowed.has(section)) {
        denied = section;
        break;
      }
    }
    if (denied) {
      last = { ok: false, reason: "section-denied", missing: denied, grantId };
      continue;
    }

    return { ok: true, grant: data, grantId };
  }

  return last;
}

/**
 * Accept either a tci-session grant (patient self-service) or a staff-proxy-cmr
 * grant (in-person nonverbal proxy). Prefer tci-session when both exist.
 */
export async function assertClientSessionCmrGrant({
  tciSessionId,
  readerUid,
  patientUid,
  requiredSections = [],
} = {}) {
  const tci = await assertClientTciSessionGrant({
    tciSessionId,
    readerUid,
    patientUid,
    requiredSections,
  });
  if (tci.ok) return { ...tci, grantKind: "tci-session" };

  const proxy = await assertClientStaffProxyCmrGrant({
    sessionId: tciSessionId,
    staffUid: readerUid,
    patientUid,
    requiredSections,
  });
  if (proxy.ok) return { ...proxy, grantKind: "staff-proxy-cmr" };

  return {
    ok: false,
    reason: proxy.reason || tci.reason || "missing",
    tciReason: tci.reason,
    proxyReason: proxy.reason,
  };
}

/**
 * Mint a tci-session token for cross-device redemption (patient device).
 * Does NOT redeem — clinician redeems via consumeShareCodeAndFetchRecord.
 */
export async function mintTciSessionTokenForCrossDevice({
  user,
  tciDoc,
  tciId,
  tciSessionId,
} = {}) {
  if (!user?.uid) {
    throw new Error("Not signed in.");
  }
  const sessionId =
    typeof tciSessionId === "string" ? tciSessionId.trim() : "";
  const tid = typeof tciId === "string" ? tciId.trim() : "";
  if (!sessionId || !tid) {
    throw new Error("tciSessionId and tciId are required.");
  }

  const topics = getTciConsentTopics(tciDoc);
  if (!topics.length) {
    throw new Error("This TCI has no CMR topics configured for consent.");
  }

  const allowedSections = {};
  for (const topic of topics) {
    allowedSections[topic.id] = true;
  }

  const token = await createShareToken({
    user,
    allowedSections,
    context: TCI_SESSION_TOKEN_CONTEXT,
    tciSessionId: sessionId,
    tciId: tid,
  });

  return {
    ...token,
    topics,
    qrPayload: buildQrPayload({ manualCode: token.manualCode }),
  };
}

/**
 * Mint a tci-session token for the TCI's fixed topics and immediately redeem
 * (same-device self-redeem). Returns QR payload + redeem result.
 */
export async function consentAndRedeemTciSession({
  user,
  tciDoc,
  tciId,
  tciSessionId,
} = {}) {
  if (!user?.uid) {
    throw new Error("Not signed in.");
  }
  const sessionId =
    typeof tciSessionId === "string" ? tciSessionId.trim() : "";
  const tid = typeof tciId === "string" ? tciId.trim() : "";
  if (!sessionId || !tid) {
    throw new Error("tciSessionId and tciId are required.");
  }

  const topics = getTciConsentTopics(tciDoc);
  if (!topics.length) {
    throw new Error("This TCI has no CMR topics configured for consent.");
  }

  const allowedSections = {};
  for (const topic of topics) {
    allowedSections[topic.id] = true;
  }

  const token = await createShareToken({
    user,
    allowedSections,
    context: TCI_SESSION_TOKEN_CONTEXT,
    tciSessionId: sessionId,
    tciId: tid,
  });

  const redeemed = await consumeShareCodeAndFetchRecord({
    manualCode: token.manualCode,
  });

  return {
    ...token,
    redeemed,
    topics,
  };
}
